/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 23.06.25
 *
 */
import BaseObject from "@shared/BaseObject.js";
import {classToClass, Type} from "@qwoach/class-transformer-wrapper";
import FundingSourcesWrapper from "@tl/core/modules/commission/model/fundingSources/FundingSourcesWrapper";
import QuoteItem from "@shared/Quote/QuoteItem";
import {ModalityName, ModalityType} from "@tl/core/modules/commission/model/commission/ModalityType";
import ServiceLogItem from "@tl/core/modules/commission/model/commission/ServiceLogItem";
import {ensureArray} from "@/utils/helpers";
import {InvoiceMode} from "@shared/Invoice/InvoiceMode";
import {ServiceType} from "@shared/Quote/ServiceType";
import CommissionProgressInfo from "@tl/core/modules/commission/model/commission/CommissionProgressInfo";
import ServiceItem from "@shared/Quote/ServiceItem";
import {
	buildConsumedUsageGroupingKey,
	buildConsumedUsageGroupingKeyParts
} from "@tl/core/modules/commission/model/commission/ConsumedUsageGrouping";
import {buildTlswPlanSessionCountMapFromJournalEntries} from "@shared/Journal/TlswSessionPlanMismatchHelper";
import SessionPlanItem from "@shared/Journal/SessionPlanItem";
import {SessionPlanDefaultItemIdEnum} from "@shared/Journal/SessionPlanDefaultItemIdEnum";
import {SessionPlanItemTypeEnum} from "@shared/Journal/SessionPlanItemTypeEnum";
import {SessionPlanRollForwardEnum} from "@shared/Journal/SessionPlanRollForwardEnum";

const FUNDING_TRACE_PREFIX = "[FUNDING_TRACE]";
const FUNDING_TRACE_SERVICE_TYPES = [
	ServiceType.SESSION,
	ServiceType.SESSION_PREPARATION_TIME,
	ServiceType.ROUND_TRIP_MILEAGE,
	ServiceType.MATERIALS_AND_EQUIPMENT,
	ServiceType.TRAVEL_DISTANCE,
];

export default class CommissionDetailsItem extends BaseObject {

	modalityType: ModalityType = ModalityType.TLSW

	@Type(() => FundingSourcesWrapper)
	fundingSourcesWrapper: FundingSourcesWrapper = new FundingSourcesWrapper();

	@Type(() => QuoteItem)
	quoteItem: QuoteItem = new QuoteItem();

	sessionStartTimeMs: number = null;
	sessionIntervalDays: number = 14;
	sessionTimeMigrationDone: boolean = false;
	hasBirthParent2: boolean = false;

	@Type(() => CommissionProgressInfo)
	progressInfo: CommissionProgressInfo = new CommissionProgressInfo()

	@Type(() => ServiceLogItem)
	serviceLogArray: ServiceLogItem[] = [];

	@Type(() => ServiceLogItem)
	serviceLogArrayDraft: ServiceLogItem[] = [];

	defaultInvoiceMode: InvoiceMode = InvoiceMode.MONTHLY

	boundaryReminderDismissals: Object = null

	@Type(() => SessionPlanItem)
	sessionPlanLayoutItems: SessionPlanItem[] = null
	@Type(() => SessionPlanItem)
	sessionPlanArchivedLayoutItems: SessionPlanItem[] = []
	sessionPlanLayoutMigrationDone: boolean = false
	sessionPlanLayoutModeVersion: number = 0



	constructor(options = {}) {
		super(options);

		this.applyOptions(options);
	}

	getSessionPlanLayoutItems() {
		return [...(this.sessionPlanLayoutItems || [])].sort((left, right) => (left.sortIndex || 0) - (right.sortIndex || 0))
	}

	getSessionPlanArchivedLayoutItems() {
		return this.sessionPlanArchivedLayoutItems || []
	}

	migrateLegacySharedSectionModes() {
		if(this.sessionPlanLayoutModeVersion >= 1 || !Array.isArray(this.sessionPlanLayoutItems)) return false
		let legacySharedIds = new Set([
			SessionPlanDefaultItemIdEnum.TLSW_RISK,
			SessionPlanDefaultItemIdEnum.TLSW_REFLECTIONS,
		])
		let layout = this.getSessionPlanLayoutItems()
		for(let item of layout){
			if(legacySharedIds.has(item.id)) item.rollForward = SessionPlanRollForwardEnum.ALL
		}
		this.setSessionPlanLayoutItems(layout)
		this.sessionPlanLayoutModeVersion = 1
		return true
	}

	migrateAllSessionsSharedMode() {
		if(this.sessionPlanLayoutModeVersion >= 2 || !Array.isArray(this.sessionPlanLayoutItems)) return false
		let layout = this.getSessionPlanLayoutItems()
		for(let item of layout){
			if(item.rollForward === "SHARED") item.rollForward = SessionPlanRollForwardEnum.ALL
		}
		this.setSessionPlanLayoutItems(layout)
		this.sessionPlanLayoutModeVersion = 2
		return true
	}

	setSessionPlanLayoutItems(items = []) {
		let layout = (items || []).map((item, index) => new SessionPlanItem({
			...item, sortIndex: index, content: null, tags: [], isReadonly: false,
		}))
		for(let required of [
			{id: SessionPlanDefaultItemIdEnum.TLSW_BOOK_CONTENT, title: "Book content"},
		]){
			if(!layout.some(item => item.id === required.id)){
				layout.push(new SessionPlanItem({...required, itemType: SessionPlanItemTypeEnum.TEXT, sortIndex: layout.length}))
			}
		}
		let activeIds = new Set(layout.map(item => item.id))
		let archived = this.getSessionPlanArchivedLayoutItems().filter(item => !activeIds.has(item.id))
		for(let item of this.getSessionPlanLayoutItems()){
			if(!activeIds.has(item.id) && !archived.some(archivedItem => archivedItem.id === item.id)){
				archived.push(item)
			}
		}
		this.sessionPlanArchivedLayoutItems = archived
		this.sessionPlanLayoutItems = layout
		return this
	}

	migrateUsageIdentityModel() {
		let quoteItem = this.getQuoteItem()
		quoteItem.ensureServiceStructureMetadata?.()

		this.serviceLogArray = this.migrateServiceLogArrayUsageIdentity(this.serviceLogArray, quoteItem)
		this.serviceLogArrayDraft = this.migrateServiceLogArrayUsageIdentity(this.serviceLogArrayDraft, quoteItem)
	}

	migrateSessionServiceLogs() {
		let quoteItem = this.getQuoteItem()
		for (let logItem of [...ensureArray(this.serviceLogArray), ...ensureArray(this.serviceLogArrayDraft)]) {
			if (quoteItem.isLegacySessionServiceType(logItem?.serviceType)) {
				logItem.serviceType = ServiceType.SESSION
			}
		}
	}

	migrateServiceLogArrayUsageIdentity(serviceLogArray = [], quoteItem = this.getQuoteItem()) {
		serviceLogArray = ensureArray(serviceLogArray)
		return serviceLogArray.map(serviceLogItem => this.migrateServiceLogUsageIdentity(serviceLogItem, quoteItem))
	}

	migrateServiceLogUsageIdentity(serviceLogItem, quoteItem = this.getQuoteItem()) {
		if (!serviceLogItem) {
			return serviceLogItem
		}
		serviceLogItem.filterParams = serviceLogItem.filterParams || {}
		if (serviceLogItem.filterParams.sourceServiceId) {
			return serviceLogItem
		}
		let matchingServices = quoteItem?.services?.filter(item => item?.serviceType === serviceLogItem.serviceType) || []
		if (matchingServices.length === 1) {
			serviceLogItem.filterParams.sourceServiceId = matchingServices[0].id
		}
		return serviceLogItem
	}

	isServiceLogMatchingServiceItem(serviceLogItem, serviceItem) {
		if (!serviceLogItem || !serviceItem) {
			return false
		}
		let sourceServiceId = serviceLogItem.filterParams?.sourceServiceId
		return !!sourceServiceId && sourceServiceId === serviceItem.id
	}

	getLogValueTotalForServiceItem(serviceItem, mapper = (item, {isDraft}) => item.usedQuantity) {
		return this.getLogValueTotal((serviceLogItem, {isDraft}) => {
			if (!this.isServiceLogIncludedByDraftState(isDraft)) {
				return
			}
			if (!this.isServiceLogMatchingServiceItem(serviceLogItem, serviceItem)) {
				return
			}
			return mapper(serviceLogItem, {isDraft})
		})
	}

	isServiceLogIncludedByDraftState(isDraft, {
		includeDraft = true,
		includeSubmitted = true,
	} = {}) {
		if (isDraft) {
			return includeDraft
		}
		return includeSubmitted
	}

	getConsumedAmountCents({
		includeDraft = true,
		includeSubmitted = true,
		excludeInvoiceTotals = true,
	} = {}) {
		return this.getLogValueTotal((serviceLogItem, {isDraft}) => {
			if (!this.isServiceLogIncludedByDraftState(isDraft, {includeDraft, includeSubmitted})) {
				return
			}
			if (excludeInvoiceTotals && serviceLogItem.serviceType === ServiceType.INVOICE_TOTAL_AMOUNT) {
				return
			}
			return serviceLogItem.usedPriceCents
		})
	}

	getProjectedConsumedAmountCents() {
		return this.getConsumedAmountCents()
	}

	getConsumedAmountCentsForServiceItem(serviceItem, {
		includeDraft = true,
		includeSubmitted = true,
	} = {}) {
		return this.getLogValueTotalForServiceItem(serviceItem, (serviceLogItem, {isDraft}) => {
			if (!this.isServiceLogIncludedByDraftState(isDraft, {includeDraft, includeSubmitted})) {
				return
			}
			return serviceLogItem.usedPriceCents
		})
	}

	getConsumedUsageValueForServiceItem(serviceItem, {
		includeDraft = true,
		includeSubmitted = true,
	} = {}) {
		return this.getLogValueTotalForServiceItem(serviceItem, (serviceLogItem, {isDraft}) => {
			if (!this.isServiceLogIncludedByDraftState(isDraft, {includeDraft, includeSubmitted})) {
				return
			}
			return serviceLogItem.usedQuantity
		})
	}

	getServiceLogItemsForServiceItem(serviceItem, {
		includeDraft = true,
		includeSubmitted = true,
	} = {}) {
		let matchingItems = []
		if (includeSubmitted) {
			matchingItems.push(...this.serviceLogArray.filter(serviceLogItem =>
				this.isServiceLogMatchingServiceItem(serviceLogItem, serviceItem)
			))
		}
		if (includeDraft) {
			matchingItems.push(...this.serviceLogArrayDraft.filter(serviceLogItem =>
				this.isServiceLogMatchingServiceItem(serviceLogItem, serviceItem)
			))
		}
		return matchingItems
	}

	buildConsumedSummaryServiceItem(serviceItem, {
		includeDraft = true,
		includeSubmitted = true,
	} = {}) {
		if (!serviceItem) {
			return serviceItem
		}

		let summaryItem = classToClass(serviceItem)
		let ownLogItems = this.getServiceLogItemsForServiceItem(serviceItem, {
			includeDraft,
			includeSubmitted,
		})
		let shouldGroupOwnUsage = !summaryItem.isUsageByPrice()
		let ownSummarySubItems = shouldGroupOwnUsage
			? this.buildConsumedSummarySubItems(serviceItem, ownLogItems)
			: []
		let childSummarySubItems = (serviceItem.subLineItems || [])
			.map(childServiceItem => this.buildConsumedSummaryServiceItem(childServiceItem, {
				includeDraft,
				includeSubmitted,
			}))
			.filter(childServiceItem => {
				let hasUsage = (Number(childServiceItem?.getUsageValue?.()) || 0) > 0
				let hasPrice = (Number(childServiceItem?.getTotalPriceCents?.()) || 0) > 0
				return hasUsage || hasPrice
			})

		summaryItem.subLineItems = [
			...ownSummarySubItems,
			...childSummarySubItems,
		]
		summaryItem.setUsageValue(this.getConsumedUsageValueFromLogItems(ownLogItems))
		if (shouldGroupOwnUsage && ownSummarySubItems.length > 0) {
			this.clearConsumedSummaryOwnAmount(summaryItem)
		}

		if (ownSummarySubItems.length > 0) {
			summaryItem._excludeParentLineInBreakdown = true
		}

		return new ServiceItem(summaryItem)
	}

	buildConsumedSummarySubItems(serviceItem, serviceLogItems = []) {
		let groups = this.groupServiceLogItemsByConsumedUsageKey(serviceLogItems)
		return Object.entries(groups).map(([groupKey, groupItems]) =>
			this.createConsumedSummarySubItem(serviceItem, groupItems, groupKey)
		)
	}

	groupServiceLogItemsByConsumedUsageKey(serviceLogItems = []) {
		let groups = {}
		for (let serviceLogItem of ensureArray(serviceLogItems)) {
			let keyParts = [
				serviceLogItem.serviceUsageType || "",
				...buildConsumedUsageGroupingKeyParts(serviceLogItem),
			]
			let groupKey = buildConsumedUsageGroupingKey(keyParts)
			if (!groups[groupKey]) {
				groups[groupKey] = []
			}
			groups[groupKey].push(serviceLogItem)
		}
		return groups
	}

	createConsumedSummarySubItem(serviceItem, serviceLogItems = [], groupKey = "") {
		let firstLogItem = serviceLogItems[0]
		let summarySubItem = classToClass(serviceItem)
		let usageValueTotal = this.getConsumedUsageValueFromLogItems(serviceLogItems)

		summarySubItem.id = [serviceItem.id || "service", "used", groupKey].join("|")
		summarySubItem.title = null
		summarySubItem.description = null
		summarySubItem.parentId = serviceItem.id || null
		summarySubItem.sourceServiceId = serviceItem.id || summarySubItem.sourceServiceId || null
		summarySubItem.subLineItems = []
		summarySubItem.serviceUsageType = firstLogItem?.serviceUsageType || summarySubItem.serviceUsageType
		summarySubItem.itemPriceCents = firstLogItem?.itemPriceCents ?? summarySubItem.itemPriceCents ?? 0
		summarySubItem.conversionFactor = firstLogItem?.conversionFactor ?? summarySubItem.conversionFactor ?? 1

		if (summarySubItem.isUsageByQuantityNumber()) {
			summarySubItem.quantity = firstLogItem?.quantity ?? summarySubItem.quantity ?? 0
		}

		summarySubItem.setUsageValue(usageValueTotal)
		return new ServiceItem(summarySubItem)
	}

	getConsumedUsageValueFromLogItems(serviceLogItems = []) {
		return ensureArray(serviceLogItems)
			.reduce((total, serviceLogItem) => total + (Number(serviceLogItem?.usedQuantity) || 0), 0)
	}

	clearConsumedSummaryOwnAmount(serviceItem) {
		serviceItem.itemPriceCents = 0
		return serviceItem
	}

	applyConsumedUsageToServiceItemTree(serviceItem, {
		includeDraft = true,
		includeSubmitted = true,
		parentServiceItem = null,
	} = {}) {
		if (!serviceItem) {
			return serviceItem
		}
		let valueUsed = this.getConsumedUsageValueForServiceItem(serviceItem, {
			includeDraft,
			includeSubmitted,
		})
		if (parentServiceItem?.isUsageByQuantityNumber?.()) {
			valueUsed = Number(parentServiceItem.quantityNumber) || 0
		}
		serviceItem.setUsageValue(valueUsed)
		for (let childServiceItem of serviceItem.subLineItems || []) {
			this.applyConsumedUsageToServiceItemTree(childServiceItem, {
				includeDraft,
				includeSubmitted,
				parentServiceItem: serviceItem,
			})
		}
		return serviceItem
	}

	getFundingSourceById(fundingSourceId) {
		return this.getFundingSourcesWrapper()?.getFundingSourceById(fundingSourceId) || null
	}

	traceFunding(label, payload = {}) {
		try {
			console.log(`${FUNDING_TRACE_PREFIX} ${label} ${JSON.stringify(payload)}`)
		} catch (error) {
			console.log(`${FUNDING_TRACE_PREFIX} ${label}`, payload, error)
		}
	}

	getServiceUsageConsumedAmountCentsUpToDate(invoiceDateMs = null) {
		return this.getLogValueTotal((serviceLogItem, {isDraft}) => {
			if (!this.isServiceLogIncludedByDraftState(isDraft)) {
				return
			}
			if (serviceLogItem.serviceType === ServiceType.INVOICE_TOTAL_AMOUNT) {
				return
			}
			if (invoiceDateMs && serviceLogItem.serviceDateMs && serviceLogItem.serviceDateMs > invoiceDateMs) {
				return
			}
			return serviceLogItem.usedPriceCents
		})
	}

	getInvoiceTotalsCentsForEstimate(invoiceDateMs = null) {
		return this.getLogValueTotal((serviceLogItem, {isDraft}) => {
			if (serviceLogItem.serviceType !== ServiceType.INVOICE_TOTAL_AMOUNT) {
				return
			}
			if (invoiceDateMs && isDraft && serviceLogItem.serviceDateMs > invoiceDateMs) {
				return
			}
			return serviceLogItem.usedPriceCents
		})
	}

	getServiceLogSummary({
		includeInvoiceTotals = false,
		invoiceDateMs = null,
	} = {}) {
		let summaryMap = {}
		let logEntries = [
			...this.serviceLogArray.map(item => ({...item, _isDraft: false})),
			...this.serviceLogArrayDraft.map(item => ({...item, _isDraft: true})),
		]
		for (let logItem of logEntries) {
			if (!includeInvoiceTotals && logItem.serviceType === ServiceType.INVOICE_TOTAL_AMOUNT) {
				continue
			}
			if (includeInvoiceTotals && logItem.serviceType !== ServiceType.INVOICE_TOTAL_AMOUNT) {
				continue
			}
			if (invoiceDateMs && logItem.serviceDateMs && logItem.serviceDateMs > invoiceDateMs) {
				continue
			}
			let key = [
				logItem.serviceType || "UNKNOWN",
				logItem.filterParams?.sourceServiceId || logItem.filterParams?.recipientId || logItem.id || "NO_ID",
				logItem._isDraft ? "draft" : "submitted",
			].join(":")
			if (!summaryMap[key]) {
				summaryMap[key] = {
					key,
					serviceType: logItem.serviceType,
					sourceServiceId: logItem.filterParams?.sourceServiceId || null,
					recipientId: logItem.filterParams?.recipientId || null,
					isDraft: logItem._isDraft,
					entryCount: 0,
					usedQuantityTotal: 0,
					usedPriceCentsTotal: 0,
				}
			}
			summaryMap[key].entryCount += 1
			summaryMap[key].usedQuantityTotal += Number(logItem.usedQuantity) || 0
			summaryMap[key].usedPriceCentsTotal += Number(logItem.usedPriceCents) || 0
		}
		return Object.values(summaryMap).sort((a, b) => b.usedPriceCentsTotal - a.usedPriceCentsTotal)
	}

	getFundingTraceSnapshot({
		invoiceDateMs = null,
	} = {}) {
		let quoteTotalAmountCents = this.getQuoteTotalAmountCents()
		let projectedConsumedAmountCents = this.getProjectedConsumedAmountCents()
		let projectedConsumedAmountCentsUpToDate = this.getServiceUsageConsumedAmountCentsUpToDate(invoiceDateMs)
		let invoiceTotalsForEstimateCents = this.getInvoiceTotalsCentsForEstimate(invoiceDateMs)

		return {
			invoiceDateMs,
			quoteTotalAmountCents,
			projectedConsumedAmountCents,
			projectedRemainingAmountCents: quoteTotalAmountCents - projectedConsumedAmountCents,
			projectedConsumedAmountCentsUpToDate,
			projectedRemainingAmountCentsUpToDate: quoteTotalAmountCents - projectedConsumedAmountCentsUpToDate,
			invoiceTotalsForEstimateCents,
			invoiceEstimateRemainingAmountCents: quoteTotalAmountCents - invoiceTotalsForEstimateCents,
			serviceUsageSummary: this.getServiceLogSummary({includeInvoiceTotals: false, invoiceDateMs}),
			invoiceTotalsSummary: this.getServiceLogSummary({includeInvoiceTotals: true, invoiceDateMs}),
			submittedLogsCount: this.serviceLogArray.length,
			draftLogsCount: this.serviceLogArrayDraft.length,
		}
	}

	serviceLogRecalculateInvoiceDraft(invoiceItem){
		this.traceFunding("CommissionDetailsItem.serviceLogRecalculateInvoiceDraft.input", {
			invoiceId: invoiceItem?.id || null,
			invoiceDateMs: invoiceItem?.invoiceDateMs || null,
			relevantItems: this.getRelevantDraftLineItemsTrace(invoiceItem?.lineItems || []),
		})
		if(!invoiceItem.isDraft()) {
			return
		}
		for(let lineItem of invoiceItem.lineItems) {
			// let limitService = ServiceToMaxValueMap[lineItem.serviceType]
			// if(limitService){
				this.serviceLogAddLineItemsDraft(lineItem);
			// }
		}
		this.serviceLogAddInvoiceItemsDraft(invoiceItem);
	}

	serviceLogResetDraft() {
		this.traceFunding("CommissionDetailsItem.serviceLogResetDraft.before", {
			currentRelevantSummary: this.getServiceLogSummary({includeInvoiceTotals: false})
				.filter(item => FUNDING_TRACE_SERVICE_TYPES.includes(item.serviceType)),
			currentDraftLogsCount: this.serviceLogArrayDraft.length,
		})
		this.serviceLogArrayDraft = [];
		this.traceFunding("CommissionDetailsItem.serviceLogResetDraft.after", {
			currentDraftLogsCount: this.serviceLogArrayDraft.length,
		})
	}

	serviceLogSyncDraftLineItems(draftLineItems = [], options = {}) {
		this.traceFunding("CommissionDetailsItem.serviceLogSyncDraftLineItems.input", {
			source: options.source || "unknown",
			relevantItems: this.getRelevantDraftLineItemsTrace(draftLineItems),
		})
		this.serviceLogResetDraft()
		this.serviceLogAddLineItemsDraft(draftLineItems)
	}

	serviceLogMoveFromDraft(invoiceItem) {
		let lineItems = invoiceItem.lineItems;
		let itemsToMove = this.serviceLogArrayDraft.filter(serviceItem => serviceItem.id === invoiceItem.id || lineItems.some(lineItem => lineItem.id === serviceItem.id || lineItem.id === serviceItem.parentId));
		console.log("[INVOICE_SUBMIT_DIAGNOSTICS]", "CommissionDetailsItem.serviceLogMoveFromDraft.before", JSON.stringify({
			invoiceId: invoiceItem?.id || null,
			itemsToMove: itemsToMove.map(serviceItem => ({
				id: serviceItem?.id || null,
				parentId: serviceItem?.parentId || null,
				serviceType: serviceItem?.serviceType || null,
				title: serviceItem?.title || null,
				usedQuantity: serviceItem?.usedQuantity ?? null,
				usedPriceCents: serviceItem?.usedPriceCents ?? null,
			})),
			draftLogCount: this.serviceLogArrayDraft.length,
			submittedLogCount: this.serviceLogArray.length,
		}));
		if (itemsToMove.length > 0) {
			this.serviceLogArrayDraft = this.serviceLogArrayDraft.filter(serviceItem => !itemsToMove.some(movedItem => movedItem.id === serviceItem.id ||  movedItem.id === serviceItem.parentId));
			itemsToMove = itemsToMove.filter(item => item.usedQuantity > 0 || item.usedPriceCents > 0);
			this.serviceLogArray.push(...itemsToMove);
		}
		console.log("[INVOICE_SUBMIT_DIAGNOSTICS]", "CommissionDetailsItem.serviceLogMoveFromDraft.after", JSON.stringify({
			invoiceId: invoiceItem?.id || null,
			movedItemsCount: itemsToMove.length,
			draftLogCount: this.serviceLogArrayDraft.length,
			submittedLogCount: this.serviceLogArray.length,
		}));
	}

	serviceLogAddInvoiceItemsDraft(invoiceItems = []) {
		let serviceLogItems = this.invoiceItemsToServiceLogArray(invoiceItems);
		this.traceFunding("CommissionDetailsItem.serviceLogAddInvoiceItemsDraft.input", {
			invoiceItems: ensureArray(invoiceItems).map(invoiceItem => ({
				id: invoiceItem?.id || null,
				invoiceDateMs: invoiceItem?.invoiceDateMs || null,
				totalPriceCents: invoiceItem?.getTotalPriceCents?.() || null,
				recipientId: invoiceItem?.recipientId || null,
				relevantItems: this.getRelevantDraftLineItemsTrace(invoiceItem?.lineItems || []),
			})),
			relevantSummaryBefore: this.getServiceLogSummary({includeInvoiceTotals: false})
				.filter(item => FUNDING_TRACE_SERVICE_TYPES.includes(item.serviceType)),
		})
		this._serviceLogChangeInternal(this.serviceLogArrayDraft, serviceLogItems);
	}

	serviceLogAddLineItemsDraft(lineItemsArray = []) {
		lineItemsArray = ensureArray(lineItemsArray)
			.map(draftLineItem => draftLineItem?.lineItem || draftLineItem)
			.filter(lineItem => !!lineItem)
		this.traceFunding("CommissionDetailsItem.serviceLogAddLineItemsDraft.input", {
			relevantItems: this.getRelevantDraftLineItemsTrace(lineItemsArray),
			relevantSummaryBefore: this.getServiceLogSummary({includeInvoiceTotals: false})
				.filter(item => FUNDING_TRACE_SERVICE_TYPES.includes(item.serviceType)),
		})
		let serviceLogItems = this.lineItemsToServiceLogArray(lineItemsArray);
		this._serviceLogChangeInternal(this.serviceLogArrayDraft, serviceLogItems);
		this.traceFunding("CommissionDetailsItem.serviceLogAddLineItemsDraft.after", {
			relevantSummaryAfter: this.getServiceLogSummary({includeInvoiceTotals: false})
				.filter(item => FUNDING_TRACE_SERVICE_TYPES.includes(item.serviceType)),
			currentDraftLogsCount: this.serviceLogArrayDraft.length,
		})
	}
	serviceLogRemoveLineItemsDraft(lineItemsArray = []) {
		let serviceLogItems = this.lineItemsToServiceLogArray(lineItemsArray);
		this._serviceLogChangeInternal(this.serviceLogArrayDraft, serviceLogItems, "remove");
	}
	serviceLogAddLineItems(lineItemsArray = []) {
		let serviceLogItems = this.lineItemsToServiceLogArray(lineItemsArray);
		this._serviceLogChangeInternal(this.serviceLogArray, serviceLogItems);
	}
	serviceLogRemoveLineItems(lineItemsArray = []) {
		let serviceLogItems = this.lineItemsToServiceLogArray(lineItemsArray);
		this._serviceLogChangeInternal(this.serviceLogArray, serviceLogItems, "remove");
	}


	lineItemsToServiceLogArray(lineItemsArray = []) {
		lineItemsArray = ensureArray(lineItemsArray);
		let serviceLogArray = lineItemsArray.map(lineItem => new ServiceLogItem({
			id: lineItem.id,
			parentId: lineItem.parentId,
			serviceType: lineItem.serviceType,
			serviceUsageType: lineItem.serviceUsageType,
			quantity: lineItem.quantity,
			itemPriceCents: lineItem.itemPriceCents,
			conversionFactor: lineItem.conversionFactor,
			usedQuantity: lineItem.getUsageValue(),
			usedPriceCents: lineItem.getLineItemPriceCents(), //price with no subitems
			serviceDateMs: lineItem.serviceDateMs,
			filterParams: {
				sourceServiceId: lineItem.sourceServiceId,
			},
		}));
		let subLineItems = lineItemsArray.flatMap(lineItem => lineItem.subLineItems || []);
		// console.log("CommissionDetailsItem.lineItemsToServiceLogArray: subLineItems = ", subLineItems, ", lineItemsArray = ", lineItemsArray);
		if(subLineItems.length > 0) {
			let subLineItemsServiceLogArray = this.lineItemsToServiceLogArray(subLineItems);
			serviceLogArray.push(...subLineItemsServiceLogArray);
		}
		return serviceLogArray
	}

	invoiceItemsToServiceLogArray(invoiceItemsArray = []) {
		invoiceItemsArray = ensureArray(invoiceItemsArray);
		let serviceLogArray = invoiceItemsArray.map(invoiceItem => new ServiceLogItem({
			id: invoiceItem.id,
			serviceType: ServiceType.INVOICE_TOTAL_AMOUNT,
			serviceUsageType: invoiceItem.serviceUsageType,
			quantity: invoiceItem.quantity,
			itemPriceCents: invoiceItem.itemPriceCents,
			conversionFactor: invoiceItem.conversionFactor,
			usedQuantity: invoiceItem.getTotalPriceCents(),
			usedPriceCents: invoiceItem.getTotalPriceCents(),
			serviceDateMs: invoiceItem.invoiceDateMs,
			filterParams: {
				recipientId: invoiceItem.recipientId,
			}
		}));
		return serviceLogArray;
	}

	_serviceLogChangeInternal(serviceLogArray, serviceLogArrayChange = [], operation = "add") {
		serviceLogArrayChange = ensureArray(serviceLogArrayChange);
		for(let serviceLogItem of serviceLogArrayChange) {
			let serviceLogItemIndex = serviceLogArray.findIndex(item => item.id === serviceLogItem.id);
			if (serviceLogItemIndex >= 0) {
				serviceLogArray.splice(serviceLogItemIndex, 1);
			}
			if(operation === "add") {
				serviceLogArray.push(serviceLogItem);
			}
		}
		// console.log("CommissionDetailsItem._serviceLogChangeInternal: serviceLogArray = ", JSON.stringify(serviceLogArray));
	}



	getLogValueTotal(mapper = (item, {isDraft}) => item.quantity) {

		// console.log("CommissionDetailsItem.getLogValueTotal: this = ", this);
		let res = 0

		// let serviceLogArray = this.serviceLogArray
		// let serviceLogArrayDraft = this.serviceLogArrayDraft
		// let arrayFull
		// if(typeof filter === "function") {
		// 	serviceLogArray = serviceLogArray.filter(item => filter(item, {isDraft: false}));
		// 	serviceLogArrayDraft = serviceLogArrayDraft.filter(item => filter(item, {isDraft: true}));
		// }
		// arrayFull = [...serviceLogArray, ...serviceLogArrayDraft];

		// for(let logItem of arrayFull) {
		// 	res += Number(logItem.quantity) || 0;
		// 	console.log("CommissionDetailsItem.getLogItemValueByServiceType: res = ", res, ", logItem = ", logItem);
		// }
		for(let logItem of this.serviceLogArray) {
			res += Number(mapper(logItem, {isDraft: false})) || 0;
			// console.log("CommissionDetailsItem.getLogItemValueByServiceType: res = ", res, ", logItem = ", logItem);
		}
		for(let logItem of this.serviceLogArrayDraft) {
			res += Number(mapper(logItem, {isDraft: true})) || 0;
			// console.log("CommissionDetailsItem.getLogItemValueByServiceType: res = ", res, ", logItem = ", logItem);
		}

		// console.log("CommissionDetailsItem.getLogItemValueByServiceType: res = ", res, ", arrayFull = ", arrayFull);

		return res

	}

	getRelevantDraftLineItemsTrace(draftLineItems = []) {
		return this.flattenTraceLineItems(draftLineItems)
			.map(draftLineItem => {
				let lineItem = draftLineItem?.lineItem || draftLineItem
				return {
					draftLineItemId: draftLineItem?.lineItem ? draftLineItem.id : null,
					lineItemId: lineItem?.id || null,
					parentLineItemId: lineItem?.parentId || null,
					sourceServiceId: lineItem?.sourceServiceId || null,
					serviceType: lineItem?.serviceType || null,
					serviceDateMs: lineItem?.serviceDateMs || null,
					quantityNumber: lineItem?.quantityNumber ?? null,
					quantity: lineItem?.quantity ?? null,
					itemPriceCents: lineItem?.itemPriceCents ?? null,
					lineItemPriceCents: lineItem?.getLineItemPriceCents?.() ?? null,
				}
			})
			.filter(item => FUNDING_TRACE_SERVICE_TYPES.includes(item.serviceType))
			.sort((itemA, itemB) => {
				let typeCompare = String(itemA.serviceType || "").localeCompare(String(itemB.serviceType || ""))
				if (typeCompare !== 0) {
					return typeCompare
				}
				let dateCompare = (Number(itemA.serviceDateMs) || 0) - (Number(itemB.serviceDateMs) || 0)
				if (dateCompare !== 0) {
					return dateCompare
				}
				return String(itemA.lineItemId || "").localeCompare(String(itemB.lineItemId || ""))
			})
	}

	flattenTraceLineItems(draftLineItems = []) {
		let items = ensureArray(draftLineItems)
		let flatItems = []
		for (let draftLineItem of items) {
			flatItems.push(draftLineItem)
			let lineItem = draftLineItem?.lineItem || draftLineItem
			let subLineItems = lineItem?.subLineItems || []
			if (subLineItems.length > 0) {
				flatItems.push(...this.flattenTraceLineItems(subLineItems))
			}
		}
		return flatItems
	}

	getModalityName() {
		return ModalityName[this.modalityType] || "";
	}

	// syncProgressInfoFromSessionPlanItem(sessionPlanItem) {
	// 	for(let sessionJournalEntry of sessionPlanItem.journalEntries) {
	// 		this.progressInfo.syncFromJournalEntry(sessionJournalEntry);
	// 	}
	// }

	syncFromSessionPlanItem(sessionPlanItem) {
		if (sessionPlanItem) {
			this.sessionStartTimeMs = sessionPlanItem.sessionStartTimeMs;
			this.sessionIntervalDays = sessionPlanItem.sessionIntervalDays;

			// this.syncProgressInfoFromSessionPlanItem(sessionPlanItem);
		}
	}

	syncSessionCountsFromJournalEntries(journalEntries = []) {
		let sessionCountBySourceServiceId = buildTlswPlanSessionCountMapFromJournalEntries(journalEntries)
		this.setSessionCountBySourceServiceIdMap(sessionCountBySourceServiceId)
		return sessionCountBySourceServiceId
	}

	hasSessionCountBySourceServiceIdMap() {
		return this.progressInfo?.hasSessionCountBySourceServiceIdMap?.() || false
	}

	getSessionCountBySourceServiceIdMap() {
		return this.progressInfo?.getSessionCountBySourceServiceIdMap?.() || null
	}

	setSessionCountBySourceServiceIdMap(sessionCountBySourceServiceId = null) {
		this.progressInfo = this.progressInfo || new CommissionProgressInfo()
		this.progressInfo.setSessionCountBySourceServiceIdMap(sessionCountBySourceServiceId)
		return this.getSessionCountBySourceServiceIdMap()
	}

	syncToSessionPlanItem(sessionPlanItem) {
		if (sessionPlanItem) {
			sessionPlanItem.sessionStartTimeMs = this.sessionStartTimeMs;
			sessionPlanItem.sessionIntervalDays = this.sessionIntervalDays;
		}
	}

	setQuoteItem(quoteItem) {
		this.quoteItem = quoteItem;
	}

	getQuoteItem() {
		if (!this.quoteItem) {
			this.quoteItem = new QuoteItem();
		}
		return this.quoteItem;
	}

	setFundingSourcesWrapper(fundingSourcesWrapper) {
		this.fundingSourcesWrapper = fundingSourcesWrapper;
	}

	getFundingSourcesWrapper() {
		if(!this.fundingSourcesWrapper) {
			this.fundingSourcesWrapper = new FundingSourcesWrapper();
		}
		return this.fundingSourcesWrapper;
	}

	getBoundaryReminderDismissals() {
		if (!this.boundaryReminderDismissals) {
			this.boundaryReminderDismissals = {}
		}
		return this.boundaryReminderDismissals
	}

	dismissBoundaryReminder(reminder) {
		let dismissalMap = this.getBoundaryReminderDismissals()
		let reminderKey = reminder?.key || null
		if (reminderKey) {
			dismissalMap[reminderKey] = {
				dismissedAtMs: Date.now(),
				type: reminder?.type || null,
				boundaryDateMs: reminder?.boundaryDateMs || null,
				secondaryBoundaryDateMs: reminder?.secondaryBoundaryDateMs || null,
			}
		}
		return dismissalMap
	}

	hasFundingSources() {
		let wrapper = this.getFundingSourcesWrapper()
		return wrapper && wrapper.getFundingSourcesExpanded().length > 0;
	}

	getQuoteTotalAmountCents() {
		return this.getQuoteItem().getTotalAmountCents();
	}

	amountAlreadyPaid(fundingSource) {
		return this.getLogValueTotal((serviceItem, {isDraft}) => {
			if(serviceItem.serviceType === ServiceType.INVOICE_TOTAL_AMOUNT
				// && (!fundingSource.hasFinancialYear() || fundingSource.isFinancialYearOne(serviceItem.serviceDateMs))
				// && fundingSource.id.startsWith(serviceItem.filterParams.recipientId);
			){
				return serviceItem.usedPriceCents
			}
		});
	}
}
